<template>
  <div class="laser-software-page">
    <!-- 1. Top Banner Section -->
    <div class="top-banner">
      <div class="banner-content">
        <h1 class="banner-title">{{ $t('software.laserSoftware.bannerTitle') }}</h1>
        <p class="banner-desc">
          {{ $t('software.laserSoftware.bannerDesc') }}
        </p>
        <div class="banner-img-container">
          <img src="@/assets/ruanjian/makebanner.png"
               :alt="$t('software.laserSoftware.bannerImageAlt')"
               class="banner-main-img" />
        </div>
      </div>
    </div>

    <!-- 2. Creation Section -->
    <div class="creation-section">
      <div class="section-container">
        <h2 class="section-title">{{ $t('software.laserSoftware.creationTitle') }}</h2>

        <div class="feature-list">
          <div class="feature-item"
               v-for="(item, index) in featureItems"
               :key="index">
            <div class="feature-icon-wrapper">
              <img :src="item.icon"
                   :alt="item.name"
                   class="feature-icon" />
            </div>
            <div class="feature-name">{{ item.name }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- 3. Intro Section -->
    <div class="intro-section">
      <div class="section-container">

        <div class="intro-content">
          <div class="intro-image-side">
            <img src="@/assets/ruanjian/system.png"
                 :alt="$t('software.laserSoftware.intro.imageAlt')"
                 class="intro-main-img" />
          </div>

          <div class="intro-text-side">
            <div class="intro-text-item">
              <h3 class="intro-text-title">{{ $t('software.laserSoftware.intro.items.item1.title') }}</h3>
              <p class="intro-text-desc">
                {{ $t('software.laserSoftware.intro.items.item1.desc') }}
              </p>
            </div>
            <div class="intro-text-item">
              <h3 class="intro-text-title">{{ $t('software.laserSoftware.intro.items.item2.title') }}</h3>
              <p class="intro-text-desc">
                {{ $t('software.laserSoftware.intro.items.item2.desc') }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 4. Download Section -->
    <div class="download-section">
      <div class="section-container">
        <h2 class="section-title">{{ $t('software.laserSoftware.downloadTitle') }}</h2>
        <div class="download-list">
          <div class="download-item"
               v-for="(app, index) in downloadAppItems"
               :key="index">
            <div class="app-icon-wrapper">
              <img :src="app.icon"
                   :alt="app.name"
                   class="app-icon" />
            </div>
            <div class="app-name">{{ app.name }}</div>
            <button class="download-btn"
                    @click="openUrl(app.url)">{{ $t('software.laserSoftware.downloadButton') }}</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { softTableApi } from '@/api'

export default {
  name: 'LaserSoftware',
  data() {
    return {
      softLoaded: false,
      softItems: [],
      features: [
        {
          key: 'proDesignTool',
          icon: require('@/assets/ruanjian/makeicon1.png')
        },
        {
          key: 'highPrecisionParams',
          icon: require('@/assets/ruanjian/makeicon2.png')
        },
        {
          key: 'multiDeviceControl',
          icon: require('@/assets/ruanjian/makeicon3.png')
        },
        {
          key: 'assetLibrary',
          icon: require('@/assets/ruanjian/makeicon4.png')
        }
      ],
      downloadApps: [
        {
          key: 'lightMakerApp',
          icon: 'https://cdn-icons-png.flaticon.com/512/732/732200.png'
        },
        {
          key: 'laserMakerLightSystem',
          icon: 'https://cdn-icons-png.flaticon.com/512/888/888859.png'
        }
      ]
    }
  },
  mounted() {
    this.loadSoftTable()
  },
  computed: {
    featureItems() {
      return this.features.map((item) => ({
        icon: item.icon,
        name: this.$t(`software.laserSoftware.features.${item.key}`),
      }));
    },
    downloadAppItems() {
      if (this.softLoaded) {
        return this.softItems
          .filter((item) => item && item.name)
          .filter((item) => {
            if (typeof item.state === 'number' && item.state !== 0) return false
            const normalized = String(item.name || '')
              .replace(/[`"'’“”、，,。.\s_-]+/g, '')
              .toLowerCase()
            return normalized.includes('lightmaker')
          })
          .map((item) => ({
            id: item.id,
            name: String(item.name || ''),
            icon: this.normalizeUrl(item.photoUrl) || '',
            url: this.normalizeUrl(item.url) || '',
            version: item.version || '',
            system: item.system || '',
            type: item.type || '',
            createTime: item.createTime || '',
          }))
      }

      return this.downloadApps.map((app) => ({
        icon: app.icon,
        name: this.$t(`software.laserSoftware.downloadApps.${app.key}`),
        url: '',
      }))
    },
  },
  methods: {
    normalizeUrl(url) {
      if (!url) return ''
      const str = String(url).trim()
      if (!str) return ''
      const noBackticks = str.replace(/`/g, '').trim()
      return noBackticks.replace(/^["']+|["']+$/g, '').trim()
    },
    openUrl(url) {
      const cleaned = this.normalizeUrl(url)
      if (!cleaned) return
      window.open(encodeURI(cleaned), '_blank')
    },
    async loadSoftTable() {
      try {
        const result = await softTableApi.selectAll()
        const ok =
          result &&
          (result.code === 200 ||
            result.success === true ||
            result.ok === true)
        const list = ok && Array.isArray(result.data) ? result.data : []
        this.softItems = list
        this.softLoaded = true
      } catch (e) {
        this.softLoaded = false
        this.softItems = []
      }
    },
  },
}
</script>

<style scoped>
.laser-software-page {
  width: 100%;
  font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
  overflow-x: hidden;
  background-color: #fff;
}

.section-container {
  width: 1200px;
  margin: 0 auto;
}

/* 1. Top Banner Section */
.top-banner {
  width: 100%;
  height: 880px;
  background-color: #fff; /* Assumed white based on image description */
  position: relative;
  text-align: center;
  padding-top: 80px;
}

.banner-content {
  width: 1200px;
  margin: 0 auto;
  position: relative;
  height: 100%;
}

.banner-title {
  font-size: 48px;
  color: #2ea2fa;
  font-weight: bold;
  margin-bottom: 30px;
}

.banner-desc {
  font-size: 24px;
  color: #333;
  line-height: 1.5;
  margin-bottom: 60px;
}

.banner-img-container {
  width: 1200px;
  height: 634px;
  position: absolute;
  left: 0;
  bottom: -64px; /* Overflow by 134px */
  z-index: 10;
  /* box-shadow: 0 20px 50px rgba(0,0,0,0.1); */ /* Optional shadow */
}

.banner-main-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: 20px; /* Optional radius */
}

/* 2. Creation Section */
.creation-section {
  width: 100%;
  height: 502px;
  background-color: #f5fbff; /* Light blue background like previous component */
  padding-top: 100px; /* 134px gap from top + spacing */

  margin-top: 0;

  box-sizing: border-box;
}

.section-title {
  font-size: 34px;
  color: #333;
  font-weight: bold;
  text-align: center;
  margin-bottom: 60px;
}

.feature-list {
  display: flex;
  justify-content: space-between;
  width: 1200px;
  margin: 0 auto;
}

.feature-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 250px;
}

.feature-icon-wrapper {
  width: 74px;
  height: 74px;
  margin-bottom: 20px;
}

.feature-icon {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.feature-name {
  width: 180px; /* Approximate based on "140x30" request, giving more width for text */
  text-align: center;
  font-size: 20px;
  color: #333;
  line-height: 1.5;
}

/* 3. Intro Section */
.intro-section {
  width: 100%;
  height: 709px;
  background-color: #fff;
  display: flex;
  align-items: center; /* Center vertically if needed, or use padding */
  padding-top: 80px;
}

.intro-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 40px;
}

.intro-image-side {
  width: 650px;
  height: 509px;
}

.intro-main-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 20px;
}

.intro-text-side {
  width: 450px; /* Remaining space roughly */
  padding-left: 50px;
}

.intro-text-item {
  margin-bottom: 50px;
}

.intro-text-title {
  font-size: 20px;
  font-weight: bold;
  color: #333;
  margin-bottom: 15px;
}

.intro-text-desc {
  font-size: 20px;
  font-weight: normal;
  color: #333;
  line-height: 1.6;
}

/* 4. Download Section */
.download-section {
  width: 100%;
  height: 493px;
  background-color: #fff; /* Or generic light? User didn't specify bg, but usually alternate. 
                             However, previous was white. Let's stick to white or consistent style.
                             If Creation is light blue, Intro is white, Download could be light blue?
                             User only specified Intro bg is #fff.
                             Let's check previous component. Download was white.
                             I'll keep it white for now unless specified.
                          */
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.download-list {
  display: flex;
  justify-content: center;
  gap: 100px;
  margin-top: 60px;
}

.download-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.app-icon-wrapper {
  width: 80px;
  height: 80px;
  margin-bottom: 20px;
}

.app-icon {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.app-name {
  font-size: 16px;
  color: #333;
  margin-bottom: 25px;
}

.download-btn {
  width: 126px;
  height: 41px;
  background-color: #2ea2fa;
  border-radius: 21px;
  border: none;
  color: #fff;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.download-btn:hover {
  background-color: #258cd6;
}
</style>
